import { PAGE_SX } from '../components/pageLayout';
import React, { useEffect, useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { DataGrid, useGridApiRef } from "@mui/x-data-grid";
import { IconButton, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import UpdateIcon from '@mui/icons-material/Update';
import { format } from 'date-fns';

import Header from "../components/Header";
import PageBanner from '../components/PageBanner';
import FlexBetween from '../components/FlexBetween';
import CustomColumnMenu from "../components/DataGridCustomColumnMenu";
import DataGridCustomToolbar from "../components/DataGridCustomToolbar";
// import { useDeleteServerIPMutation, useGetFirstServerIPsQuery, useUpdateServerIPStatusMutation } from "../state/api";
import { useNavigate, useParams } from "react-router-dom";
import DeleteConfirmationDialog from "../components/DeleteConfirmationDialog";
import ToggleSelect from "../components/ToggleSelect";
import CustomPagination from "../components/CustomPagination";
import { updateSubProtocal } from "../state/curlApi";
import SubProtocolCell from "../components/SubProtocolCell";
import { useAuthContext } from "../context/auth";
import { usePathContext } from "../context/path";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import { useFirstServerIPsContext } from "../context/firstServerIPs";
import useGridStyles from "../hooks/useGridStyles";

const VPNServerIPs = () => {
  const { serverId } = useParams();

  // theme
  const theme = useTheme();
  const { gridSx, gridBorder } = useGridStyles();
  const { auth } = useAuthContext();
  const { setTitle } = usePathContext()
  const { firstServersIPs, dispatch } = useFirstServerIPsContext()
  const axiosPrivate = useAxiosPrivate()
  const [ notFound, setNotFound ] = useState(false)

  const navigate = useNavigate();

  // Dialog state
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedServerIPId, setSelectedServerIPId] = useState(null);

  // values for backend
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [sort, setSort] = useState({});
  const [search, setSearch] = useState("");
  
  const apiRef = useGridApiRef();

  // search
  const [searchInput, setSearchInput] = useState("");

  useEffect(() => {
    setTitle("VPNServerIPs Management")
    
    const abortController = new AbortController()
    const getFirstServerIPs = async () => {
      try {
        const response = await axiosPrivate.get('/api/vpnManagement/getfirstserverips', {
          params: { page, pageSize, sort: JSON.stringify(sort), search, serverId},
          signal: abortController.signal
        })
        dispatch({type: 'SET_FIRSTSERVERIPS', payload: response.data})
        setNotFound(false)
      } catch (err) {
        // console.log(err)
        setNotFound(true)
      }
    }

    if(auth){
        getFirstServerIPs()
    }

    return () => {
      abortController.abort()
    }
  },[auth, axiosPrivate, dispatch, page, pageSize, search, serverId, setTitle, sort]);

  const handleUpdateSubProtocal = async (id, ip, subproto) => {
    try {
      const result = await updateSubProtocal(id, ip, subproto);
      console.log('result', result);
    } catch (error) {
      console.error("Failed to update subproto:", error);
    }
  }
  
  // data columns
  const columns = [
    {
      field: "_id",
      headerName: "ID",
      flex: 0.7,
      renderCell: (params) => (
        <div>
            {params.value.slice(-5)}
        </div>
      ),
    },
    {
      field: "server_id",
      headerName: "Server ID",
      flex: 0.7,
      renderCell: (params) => (
        <div>
            {params.value.slice(-5)}
        </div>
      ),
    },
    {
      field: "ip",
      headerName: "IP",
      flex: 1,
    },
    {
      field: "port",
      headerName: "Port",
      flex: 0.2,
    },
    {
      field: "country",
      headerName: "Country",
      flex: 1,
    },
    {
      field: "domain_name",
      headerName: "Domain",
      flex: 1,
    },
    {
      field: "use_domain",
      headerName: "Use?",
      flex: 0.2,
      valueFormatter: (params) => `${params === 1 ? 'Yes' : 'No'}`,
    },
    {
      field: "protocol",
      headerName: "Protocol",
      flex: 0.5,
      renderCell: (params) => (
        <div style={{ color: '#FF2727' }}>
          {params.value === 1 ? 'TLS' : 'QUIC'}
        </div>
      ),
    },
    {
      field: "sub_protocol",  // in ovpn "conn-retrymax 2"
      headerName: "SubProtocol",
      // type: "actions",
      renderCell: (params) => (
        <SubProtocolCell
            value={params.value}
            row={params.row}
            onUpdate={(id, newValue) => {
              const serverIP = params.row.ip;
              // Handle the update logic here
              console.log(`Updating row ${serverIP}, ${id} with new value ${newValue}`);
              handleUpdateSubProtocal(id, serverIP, newValue)
            }}
        />
      ),
    },
    {
      field: "fake_domain",
      headerName: "Fake Domain",
    },
    {
      field: "use_fakedomain",
      headerName: "Use?",
      flex: 0.2,
      valueFormatter: (params) => `${params === 1 ? 'Yes' : 'No'}`,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.9,
      cellClassName: "status-cell",
      renderCell: (params) => (
        <ToggleSelect
          initialStatus={params.value}
          onUpdateStatus={async (newStatus) => {
            await handleServerIPStatusChange(
              params.row._id,
              newStatus
            );
          }}
        />
      ),
    },
    {
      field: "last_checktime",
      headerName: "Checked At",
      flex: 1.0,
      renderCell: (params) => {
        if(params.value)
          return format(new Date(params.value), 'yyyy-MM-dd HH:mm:ss');
        else
          return "";
      }
    },
    {
      field: "created_at",
      headerName: "Created_At",
      flex: 1.0,
      renderCell: (params) => {
        return format(new Date(params.value), 'yyyy-MM-dd HH:mm:ss');
      }
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 110,
      renderCell: (params) => (
        <div>
          <Tooltip title="Update Check time">
            <IconButton
              color="secondary"
              size="small"
              style={{ marginRight: 8 }}
              onClick={() => handleRefresh(params.row._id)}
            >
              <UpdateIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit Server IP">
            <IconButton
              color="secondary"
              size="small"
              style={{ marginRight: 8 }}
              onClick={() => handleEdit(params.row.server_id, params.row._id)}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Server IP">
            <IconButton
              color="secondary"
              size="small"
              onClick={() => handleDelete(params.row._id)}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </div>
      ),
    }
  ];

  const handleRefresh = async (id) => {
    // Logic for editing the row with the given id
    // console.log(`Refresh row with id: ${id}`);
    try {
      const newStatus = 3;
      const response = await axiosPrivate.patch('/api/vpnManagement/updatefirstserverip', {
        params: { 
          page, pageSize, sort: JSON.stringify(sort), search, serverId,
          serverIP_id: id, newStatus 
        },
      });
      dispatch({ type: 'UPDATE_FIRSTSERVERIPS', payload: response.data });
      // console.log('ServerIP status updated successfully');
      // Optionally update the UI or state with the new status
    } catch (error) {
      console.error('Failed to update serverIP status:', error);
      // Handle error (e.g., show error message to user)
    }
  };

  const handleEdit = (serverId, serverIPId) => {
    const serverIP = firstServersIPs.combinedData?.find(
      (serverIP) => serverIPId === serverIP._id
    );

    navigate(`/editserverip/:${serverId}/:${serverIPId}`, {state: {serverIP: serverIP}});
  };
  
  const handleDelete = (id) => {
    // Logic for deleting the row with the given id
    console.log(`Delete row with id: ${id}`);
    handleOpenDialog(id);
  };

  // Open delete confirmation dialog
  const handleOpenDialog = (serverIPId) => {
    setSelectedServerIPId(serverIPId);
    setOpenDeleteDialog(true);
  };

  // Close delete confirmation dialog
  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedServerIPId(null);
  };

  // Handle delete server
  const handleDeleteServerIPs = async () => {
    try {
      // console.log('selectedServerIPId', selectedServerIPId)
      const response = await axiosPrivate.delete(`/api/vpnManagement/deletefirstserverip/${selectedServerIPId}`, {
        params: { serverId },
      });
      dispatch({ type: 'DELETE_FIRSTSERVERIPS', payload: response.data });
      // Refetch or update state after deletion
      setSearch(search); // This will refetch data with the current search term
      handleCloseDeleteDialog();
    } catch (error) {
      console.error("Failed to delete the server:", error);
    }
  };
  
  const handleServerIPStatusChange = async (serverIPId, newStatus) => {
    try {
      const response = await axiosPrivate.patch('/api/vpnManagement/updatefirstserverip', {
        params: { 
          page, pageSize, sort: JSON.stringify(sort), search, serverId,
          serverIP_id: serverIPId, newStatus 
        },
      });
      dispatch({ type: 'UPDATE_FIRSTSERVERIPS', payload: response.data });
      // console.log('ServerIP status updated successfully');
      // Optionally update the UI or state with the new status
    } catch (error) {
      console.error('Failed to update serverIP status:', error);
      // Handle error (e.g., show error message to user)
    }        
  }

  // loader
  if (!firstServersIPs) {
    return (
      <Typography variant="h5" mt="20%" textAlign="center">
        Loading...
      </Typography>
    );
  }

  return (
    <Box sx={PAGE_SX}>
      {/* Header */}
      <FlexBetween>
        <Header title="VPN SERVER IPS" subtitle="VPN Server IPs Management" />
        <PageBanner />
      </FlexBetween>

      {/* Content */}
      <Box
        mt="1rem"
        flex={1}
        minHeight="400px"
        backgroundColor={theme.palette.background.alt}
        border={gridBorder}
        borderRadius="0.55rem"
        overflow="hidden"
      >
        {/* Grid Table */}
        <DataGrid
          loading={!firstServersIPs}
          getRowId={(row) => row._id}
          rows={(firstServersIPs && firstServersIPs.combinedData) || []}
          columns={columns}
          rowCount={(firstServersIPs && firstServersIPs.total) || 0}
          rowsPerPageOptions={[20, 50, 100]}
          pagination
          page={page}
          pageSize={pageSize}
          paginationMode="server"
          sortingMode="server"
          onPageChange={(newPage) => setPage(newPage)}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          onSortModelChange={(newSortModel) => setSort(...newSortModel)}
          components={{
            Toolbar: DataGridCustomToolbar,
            ColumnMenu: CustomColumnMenu,
            Pagination: CustomPagination, // Use the custom pagination component
          }}
          componentsProps={{
            pagination: { 
              apiRef, 
              count: firstServersIPs?.total, 
              page, 
              pageSize, 
              onPageChange: setPage,
              onPageSizeChange: setPageSize,
            },
            toolbar: { searchInput, setSearchInput, setSearch },
          }}
          sx={{
            ...gridSx,
            "& .status-cell": {
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            },
          }}
        />
      </Box>

      <DeleteConfirmationDialog
        open={openDeleteDialog}
        title="Delete server IP?"
        handleClose={handleCloseDeleteDialog}
        handleDelete={handleDeleteServerIPs}
      />
    </Box>
  )
}

export default VPNServerIPs;